/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {areEqual} from 'react-window';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {MapLocateIcon} from '@ion/dls/src/icons/MapLocateIcon';
import {mapViewGoToOptions} from '@ion/dls/src/maps/constants';
import {useLocationMarker} from '@ion/dls/src/maps/hooks/useLocationMarker';
import {IconSize, MarkerProps} from '@ion/dls/src/types/types';

type LocationToolProps = Omit<MarkerProps, 'color'>;

// defines the prop definition for LocationToolFactory component...
export interface LocationToolFactoryProps {
  view: __esri.MapView;
  map: __esri.Map;
};

/**
 * The LocationTool provides the UI for Location icon that can be placed on the
 * map layers.
 *
 * ```typescript
 *
 *   <LocationTool size={} onClick={function() {}} />
 *
 * ```
 */
export const LocationTool = ({size, onClick}: LocationToolProps) => (
  <FloatingIconButtonGroup>
    <FloatingIconButtonGroup.FloatingIconButton onClick={onClick}>
      <MapLocateIcon size={size} />
    </FloatingIconButtonGroup.FloatingIconButton>
  </FloatingIconButtonGroup>
);

/**
 * The LocationToolFactory component is responsible for creating a Location Tool/widget
 * component that provide an option to move the map to current location. This is a stand-alone
 * tool, can be usable in any of position on the map.
 *
 * ```typescript
 *
 *  import LocationToolFactory from '../tools/LocationTool';
 *
 *  <LocationToolFactory view={view} map={map} />
 *
 * ```
 */
export const LocationToolFactory = ({
  view,
  map
}: LocationToolFactoryProps) => {
  const [addMarker] = useLocationMarker(view, map, true);

  const locationSuccess = useCallback((position) => {
    const latitude  = position.coords.latitude;
    const longitude = position.coords.longitude;
    addMarker(latitude, longitude, '');
    view.goTo({
      center : [longitude, latitude],
      zoom   : 15
    }, mapViewGoToOptions);
  }, []);

  const moveToCurrentPosition = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        locationSuccess,
        (err) => { error(err); },
        {
          enableHighAccuracy : true
        });
    }
  }, []);

  return (
    <Tooltip title={translate('maps:controls.location')}>
      <div>
        <LocationTool size={IconSize.medium} onClick={moveToCurrentPosition} />
      </div>
    </Tooltip>
  );
};

export default memo(LocationToolFactory, areEqual);
